import React, { createElement } from 'react';
import './index.css';
import { render } from 'react-dom';
import { App } from './App';
/**
 * CRITICAL above-the-fold CSS for the hero, inlined synchronously into the
 * <head>. This means the hero can paint correctly even before the bundled
 * Tailwind CSS arrives — collapsing FCP from "wait for CSS bundle" to
 * "wait for JS bundle" (~1s target on a fast 4G mobile).
 *
 * Only the mobile styles for the elements actually rendered by <Header>
 * + <Hero> are included. Everything else upgrades once the main stylesheet
 * arrives (which is deferred to non-blocking, see step 2 below).
 *
 * IMPORTANT: This CSS uses the same Tailwind class names as the JSX so
 * it auto-applies without any source changes. When the full bundle arrives,
 * its rules cascade and take over (same specificity, later in source order).
 */
const CRITICAL_CSS = `
*,::before,::after{box-sizing:border-box;border:0 solid #e5e7eb}
*{margin:0;padding:0}
html{line-height:1.5;-webkit-text-size-adjust:100%;font-family:'Inter',system-ui,-apple-system,'Segoe UI',Roboto,sans-serif}
body{background:#FDF8F2;color:#1F1F1F;-webkit-font-smoothing:antialiased;min-height:100vh;margin:0}
h1,h2,h3,h4,p,span{font:inherit;margin:0}
img{display:block;max-width:100%;height:auto}
button{cursor:pointer;font:inherit;background:transparent;border:0;color:inherit;padding:0}
a{color:inherit;text-decoration:none}
em{font-style:italic;color:inherit}
.bg-cream{background-color:#FDF8F2}
.min-h-screen{min-height:100vh}
.relative{position:relative}
.min-h-\\[85vh\\]{min-height:85vh}
.flex{display:flex}
.items-center{align-items:center}
.items-start{align-items:flex-start}
.justify-center{justify-content:center}
.pt-24{padding-top:6rem}
.pb-12{padding-bottom:3rem}
.overflow-hidden{overflow:hidden}
.max-w-7xl{max-width:80rem}
.max-w-md{max-width:28rem}
.mx-auto{margin-left:auto;margin-right:auto}
.px-6{padding-left:1.5rem;padding-right:1.5rem}
.px-8{padding-left:2rem;padding-right:2rem}
.py-4{padding-top:1rem;padding-bottom:1rem}
.w-full{width:100%}
.h-full{height:100%}
.h-\\[35vh\\]{height:35vh}
.grid{display:grid}
.grid-cols-1{grid-template-columns:1fr}
.gap-8{gap:2rem}
.gap-6{gap:1.5rem}
.flex-col{flex-direction:column}
.z-10{z-index:10}
.uppercase{text-transform:uppercase}
.tracking-\\[0\\.2em\\]{letter-spacing:.2em}
.tracking-widest{letter-spacing:.1em}
.text-xs{font-size:.75rem;line-height:1rem}
.text-sm{font-size:.875rem;line-height:1.25rem}
.text-base{font-size:1rem;line-height:1.5rem}
.text-4xl{font-size:2.25rem;line-height:2.5rem}
.text-ink-light{color:#4A4A4A}
.text-ink{color:#1F1F1F}
.text-sand-dark{color:#B89665}
.text-white{color:#fff}
.mb-6{margin-bottom:1.5rem}
.mb-8{margin-bottom:2rem}
.block{display:block}
.font-serif{font-family:'Cormorant Garamond','Times New Roman',Georgia,serif}
.leading-\\[1\\.1\\]{line-height:1.1}
.leading-relaxed{line-height:1.625}
.italic{font-style:italic}
.font-light{font-weight:300}
.inline-flex{display:inline-flex}
.bg-sand{background-color:#C9A876}
.transition-colors{transition-property:color,background-color,border-color}
.duration-300{transition-duration:.3s}
.border-b{border-bottom:1px solid}
.border-transparent{border-color:transparent}
.pb-1{padding-bottom:.25rem}
.object-cover{object-fit:cover}
.rounded-t-full{border-top-left-radius:9999px;border-top-right-radius:9999px}
.bg-sage\\/20{background-color:rgba(232,240,230,.2)}
.shadow-soft-lg{box-shadow:0 25px 50px -12px rgba(0,0,0,.1)}
`;
(function bootPerfHints() {
  if (typeof document === 'undefined') return;
  const head = document.head;
  // 1. Inject critical hero CSS BEFORE anything else. This guarantees the
  //    hero can paint as soon as its DOM is mounted, even if the bundled
  //    Tailwind stylesheet has not yet finished downloading.
  const style = document.createElement('style');
  style.setAttribute('data-mp-critical', 'true');
  style.textContent = CRITICAL_CSS;
  head.appendChild(style);
  // 2. Defer the bundled Tailwind stylesheet to be non-blocking.  Switch its
  //    `media` attribute to `print` (which doesn't apply on screen), then
  //    swap back to `all` once it finishes loading.  This releases the
  //    render-block while still applying the full styling once available.
  //
  //    Note: this only helps if the link is still in flight when the script
  //    runs.  On very fast networks it may already be done — in which case
  //    nothing changes.  On slow networks it can shave 200-600 ms off FCP.
  const links = document.querySelectorAll('link[rel="stylesheet"]');
  links.forEach(link => {
    const el = link as HTMLLinkElement;
    if (el.dataset.mpDeferred === '1') return;
    if (el.id === 'mp-google-fonts') return; // already async-loaded
    el.dataset.mpDeferred = '1';
    const originalMedia = el.media || 'all';
    el.media = 'print';
    el.addEventListener('load', () => {
      el.media = originalMedia;
    }, {
      once: true
    });
  });
  // 3. Preload the hero image with high priority.  Smaller-than-DPR variant
  //    on mobile (w=400 @ q=60) — visually almost indistinguishable but ~40%
  //    lighter on the wire.
  const preload = document.createElement('link');
  preload.rel = 'preload';
  preload.as = 'image';
  preload.href = 'https://images.unsplash.com/photo-1545389336-cf090694435e?q=60&w=400&auto=format&fit=crop';
  preload.setAttribute('imagesrcset', 'https://images.unsplash.com/photo-1545389336-cf090694435e?q=60&w=400&auto=format&fit=crop 400w, https://images.unsplash.com/photo-1545389336-cf090694435e?q=65&w=720&auto=format&fit=crop 720w, https://images.unsplash.com/photo-1545389336-cf090694435e?q=70&w=1100&auto=format&fit=crop 1100w');
  preload.setAttribute('imagesizes', '(max-width: 1024px) 100vw, 50vw');
  preload.setAttribute('fetchpriority', 'high');
  head.appendChild(preload);
})();
render(<App data-id="element-225" />, document.getElementById('root'));